/**
 * Visualizador — a foto em tela, quem enviou, de que dia e atividade.
 * Baixar e compartilhar usam a URL curta do original; apagar só aparece
 * para quem pode (autor ou dono), e pede confirmação.
 *
 * Rodada 3 (B4): daqui a foto vira CAPA da atividade (quem edita o roteiro)
 * e muda de atividade — "Trocar" — para vincular depois (autor ou quem
 * edita o roteiro). É o mesmo registro: nada é copiado.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { uuidv7 } from '@nv/core';

import { usePermissao, useTrip } from '../../trip/TripContext';
import { mesclarDiaDoServidor } from '../roteiro/carregarDia';
import { Icon } from '../../ui/icons';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';
import { Card, ErrorState, Skeleton } from '../../ui/primitives';
import { memoriasApi } from './api';
import { ddmm } from './comum';
import { Sheet } from '../../ui/Sheet';

export function FotoScreen() {
  const { fotoDe } = usePessoas();
  const { dia = '', foto = '' } = useParams();
  const { tripId } = useTrip();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const [mexendo, setMexendo] = useState(false);
  // Controles visíveis (tocar na foto alterna).
  const [ui, setUi] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const editaRoteiro = usePermissao('edit_itinerary');

  const q = useQuery({ queryKey: ['memorias', tripId, 'foto', foto], queryFn: () => memoriasApi.ler(tripId, foto), staleTime: 10 * 60_000 });
  const atividades = useQuery({ queryKey: ['memorias', 'atividades', q.data?.dayId], queryFn: () => memoriasApi.atividadesDoDia(q.data?.dayId ?? ''), enabled: trocando && !!q.data?.dayId });

  async function depoisDeMexer() {
    await cliente.invalidateQueries({ queryKey: ['memorias', tripId] });
    await cliente.invalidateQueries({ queryKey: ['memorias', 'atividades'] });
    if (q.data?.dayId) await mesclarDiaDoServidor(q.data.dayId).catch(() => {});
  }

  async function trocarAtividade(activityId: string | null) {
    setMexendo(true);
    setErro(null);
    try {
      await memoriasApi.trocarAtividade(tripId, foto, activityId);
      await depoisDeMexer();
      setTrocando(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível trocar a atividade.');
    } finally {
      setMexendo(false);
    }
  }

  async function usarComoCapa(tirar = false) {
    if (!q.data?.activityId || !q.data.dayId) return;
    setMexendo(true);
    setErro(null);
    try {
      const lista = await memoriasApi.atividadesDoDia(q.data.dayId);
      const ativ = lista.find((a) => a.id === q.data?.activityId);
      if (!ativ) throw new Error('A atividade não existe mais.');
      await memoriasApi.definirCapa(tripId, ativ.id, ativ.version, tirar ? null : foto, uuidv7());
      await depoisDeMexer();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível definir a capa.');
    } finally {
      setMexendo(false);
    }
  }

  async function excluir() {
    try {
      await memoriasApi.excluir(tripId, foto);
      await cliente.invalidateQueries({ queryKey: ['memorias', tripId] });
      navegar(`/memorias/${dia}`, { replace: true });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível excluir.');
    }
  }

  async function compartilhar() {
    if (!q.data) return;
    try {
      const res = await fetch(q.data.previewUrl);
      const blob = await res.blob();
      const arquivo = new File([blob], `foto-${foto.slice(0, 8)}.jpg`, { type: blob.type || 'image/jpeg' });
      if (navigator.canShare?.({ files: [arquivo] })) {
        await navigator.share({ files: [arquivo] });
        return;
      }
      await navigator.share({ title: 'Foto da viagem', text: `Foto de ${q.data.uploader.name} · ${ddmm(dia)}` });
    } catch {
      // Cancelar o compartilhador não é erro.
    }
  }

  const quando = q.data?.takenAt ?? q.data?.createdAt ?? null;
  const horario = quando ? new Date(quando).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    // Refino final: a foto é sempre escura, em qualquer tema; tocar na foto
    // esconde e mostra os controles, que ficam sobre degradês.
    <section className={ui ? 'screen mem mem--foto' : 'screen mem mem--foto mem--foto-limpa'}>
      <header className="mem__foto-cabecalho">
        <button type="button" className="mem__foto-botao" onClick={() => navegar(`/memorias/${dia}`)} aria-label="Fechar">
          <Icon name="fechar" size={22} />
        </button>
        <span className="mem__data">{ddmm(dia)}{horario ? ` · ${horario}` : ''}</span>
        {q.data?.canDelete ? (
          <button type="button" className="mem__foto-botao mem__excluir" onClick={() => setConfirmando(true)} aria-label="Excluir foto">
            <Icon name="excluir" size={20} />
          </button>
        ) : (
          <span className="mem__foto-botao mem__foto-botao--vazio" aria-hidden="true" />
        )}
      </header>

      {q.isLoading && <Skeleton height={400} />}
      {q.isError && <ErrorState title="Foto não encontrada" description="Ela pode ter sido excluída." onRetry={() => void q.refetch()} />}
      {q.isSuccess && (
        <>
          <button type="button" className="mem__foto-palco" onClick={() => setUi((v) => !v)} aria-label={ui ? 'Esconder os controles' : 'Mostrar os controles'}>
            <img className="mem__foto" src={q.data.previewUrl} alt={`Foto de ${q.data.uploader.name}`} />
          </button>
          <div className="mem__foto-rodape">
            <div className="mem__meta">
              <FotoAvatar nome={q.data.uploader.name} url={fotoDe(q.data.uploader.memberId).url} cor={fotoDe(q.data.uploader.memberId).cor} tamanho={40} />
              <span className="mapa__pessoa-texto">
                <span className="mapa__pessoa-nome">{q.data.uploader.me ? 'Você' : q.data.uploader.name}</span>
                <span className="mapa__pessoa-familia">
                  {q.data.activityTitle ? (
                    <Link to={`/roteiro/${dia}/${q.data.activityId}`}>{q.data.activityTitle}</Link>
                  ) : (
                    <Link to={`/roteiro/${dia}`}>Dia {ddmm(dia)}</Link>
                  )}
                  {q.data.isActivityCover && <span className="mem__selo-capa nv-pop" data-testid="foto-e-capa">Capa</span>}
                  {q.data.canReassign && (
                    <button type="button" className="mem__trocar" onClick={() => setTrocando(true)} data-testid="foto-trocar-atividade">
                      · Trocar
                    </button>
                  )}
                </span>
              </span>
            </div>
            {erro && !trocando && <p className="auth-error" role="alert">{erro}</p>}
            <div className="mem__foto-acoes">
              {q.data.activityId && editaRoteiro && (
                q.data.isActivityCover ? (
                  <button type="button" className="nv-btn mem__foto-acao mem__foto-acao--capa" disabled={mexendo} onClick={() => void usarComoCapa(true)} data-testid="foto-tirar-capa">
                    Tirar da capa
                  </button>
                ) : (
                  <button type="button" className="nv-btn nv-btn--primary mem__foto-acao--capa" disabled={mexendo} onClick={() => void usarComoCapa()} data-testid="foto-usar-capa">
                    <Icon name="destaque" size={16} /> Usar como capa
                  </button>
                )
              )}
              <a className="nv-btn mem__foto-acao" href={q.data.downloadUrl} download>
                Baixar
              </a>
              {typeof navigator.share === 'function' && (
                <button type="button" className="nv-btn mem__foto-acao mem__foto-acao--icone" onClick={() => void compartilhar()} aria-label="Compartilhar">
                  <Icon name="compartilhar" size={18} />
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {trocando && q.data && (
        <Sheet titleId="mem-trocar" onFechar={() => setTrocando(false)}>
            <h2 className="sheet__title" id="mem-trocar">De que atividade é esta foto?</h2>
            <p className="sheet__text">Ela continua no álbum do dia; muda só onde aparece no roteiro.</p>
            <ul className="nav-apps" data-testid="foto-atividades">
              <li>
                <button type="button" className={`nav-apps__item${q.data.activityId === null ? ' nav-apps__item--atual' : ''}`} disabled={mexendo} onClick={() => void trocarAtividade(null)}>
                  <span className="nav-apps__nome">Nenhuma — foto do dia</span>
                  {q.data.activityId === null && <Icon name="concluir" size={16} />}
                </button>
              </li>
              {(atividades.data ?? []).map((a) => (
                <li key={a.id}>
                  <button type="button" className={`nav-apps__item${q.data?.activityId === a.id ? ' nav-apps__item--atual' : ''}`} disabled={mexendo} onClick={() => void trocarAtividade(a.id)} data-testid="foto-atividade-opcao">
                    <span className="mono">{a.startTime}</span>
                    <span className="nav-apps__nome">{a.title}</span>
                    {q.data?.activityId === a.id && <Icon name="concluir" size={16} />}
                  </button>
                </li>
              ))}
            </ul>
            {erro && <p className="auth-error" role="alert">{erro}</p>}
            <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => setTrocando(false)}>Cancelar</button>
        </Sheet>
      )}

      {confirmando && (
        <Sheet titleId="mem-excluir" onFechar={() => setConfirmando(false)} className="sheet__panel--escuro sheet__panel--confirmar">
            <span className="sheet__selo sheet__selo--perigo" aria-hidden="true"><Icon name="excluir" size={26} /></span>
            <h2 className="sheet__title" id="mem-excluir">Excluir esta foto?</h2>
            <p className="sheet__text">Ela sai do álbum do grupo agora. Não dá para desfazer.</p>
            {erro && <p className="auth-error" role="alert">{erro}</p>}
            <div className="sheet__acoes">
              <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setConfirmando(false)}>Cancelar</button>
              <button type="button" className="nv-btn nv-btn--danger" onClick={() => void excluir()}>Excluir</button>
            </div>
        </Sheet>
      )}
    </section>
  );
}
